import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Upload } from "lucide-react";

const STATUS = {
  accrued: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-blue-50 text-blue-700 border-blue-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  reversed: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function Agency() {
  const [agencies, setAgencies] = useState([]);
  const [closers, setClosers] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [openAg, setOpenAg] = useState(false);
  const [openCl, setOpenCl] = useState(false);
  const [openRec, setOpenRec] = useState(false);
  const [openRecDetail, setOpenRecDetail] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = async () => {
    const [a, cl, cm, rc] = await Promise.all([
      api.get("/agencies"), api.get("/agencies/closers"),
      api.get("/agencies/commissions"), api.get("/agencies/reconciliations"),
    ]);
    setAgencies(a.data); setClosers(cl.data); setCommissions(cm.data); setReconciliations(rc.data);
  };
  useEffect(() => { load(); }, []);

  const approveAll = async () => {
    for (const id of selectedIds) await api.post(`/agencies/commissions/${id}/approve`);
    toast.success(`Approved ${selectedIds.length}`);
    setSelectedIds([]); load();
  };

  const payoutAll = async () => {
    try {
      await api.post("/agencies/commissions/payout", {
        commission_ids: selectedIds, payout_date: new Date().toISOString().slice(0, 10),
      });
      toast.success(`Paid out ${selectedIds.length}`);
      setSelectedIds([]); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const toggle = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <>
      <PageHeader title="Agency & Commissions" subtitle="Manage agencies, closers, accrued commissions and monthly agency reconciliations." />
      <PageBody>
        <Tabs defaultValue="commissions">
          <TabsList className="mb-4">
            <TabsTrigger value="commissions" data-testid="tab-commissions">Commissions</TabsTrigger>
            <TabsTrigger value="agencies" data-testid="tab-agencies">Agencies</TabsTrigger>
            <TabsTrigger value="closers" data-testid="tab-closers">Closers</TabsTrigger>
            <TabsTrigger value="reconciliations" data-testid="tab-reconciliations">Reconciliations</TabsTrigger>
          </TabsList>

          <TabsContent value="commissions">
            {selectedIds.length > 0 && (
              <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-slate-100 rounded text-sm">
                <span>{selectedIds.length} selected</span>
                <Button size="sm" variant="outline" onClick={approveAll} data-testid="approve-commissions">Approve</Button>
                <Button size="sm" onClick={payoutAll} className="bg-emerald-600 hover:bg-emerald-700" data-testid="payout-commissions">Pay out</Button>
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Sale</TableHead>
                    <TableHead>Agency</TableHead>
                    <TableHead>Closer</TableHead>
                    <TableHead className="text-right">Base</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commissions.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">No commissions yet.</TableCell></TableRow>}
                  {commissions.map((c) => {
                    const ag = agencies.find((a) => a.id === c.agency_id);
                    const cl = closers.find((x) => x.id === c.closer_id);
                    return (
                      <TableRow key={c.id} data-testid={`commission-row-${c.id}`}>
                        <TableCell>
                          {c.status === "accrued" || c.status === "approved" ? (
                            <Checkbox checked={selectedIds.includes(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`select-commission-${c.id}`} />
                          ) : null}
                        </TableCell>
                        <TableCell className="text-xs font-mono">{c.sale_number}</TableCell>
                        <TableCell className="text-xs">{ag?.name || "—"}</TableCell>
                        <TableCell className="text-xs">{cl?.name || "—"}</TableCell>
                        <TableCell className="text-right font-mono">{fmtMoney(c.base_minor, c.currency)}</TableCell>
                        <TableCell className="text-right font-mono">{c.rate_pct}%</TableCell>
                        <TableCell className="text-right font-mono font-medium">{fmtMoney(c.amount_minor, c.currency)}</TableCell>
                        <TableCell><Badge variant="outline" className={STATUS[c.status]}>{c.status.toUpperCase()}</Badge></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="agencies">
            <div className="flex justify-end mb-3"><Button size="sm" onClick={() => setOpenAg(true)} data-testid="new-agency-button"><Plus className="w-4 h-4 mr-1.5" /> New agency</Button></div>
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Cohort</TableHead><TableHead className="text-right">Rate %</TableHead></TableRow></TableHeader>
                <TableBody>
                  {agencies.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-6">No agencies.</TableCell></TableRow>}
                  {agencies.map((a) => (
                    <TableRow key={a.id}><TableCell className="font-mono text-xs">{a.code}</TableCell><TableCell>{a.name}</TableCell><TableCell className="text-xs">{a.cohort}</TableCell><TableCell className="text-right font-mono">{a.rate_pct}%</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="closers">
            <div className="flex justify-end mb-3"><Button size="sm" onClick={() => setOpenCl(true)} data-testid="new-closer-button"><Plus className="w-4 h-4 mr-1.5" /> New closer</Button></div>
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Agency</TableHead><TableHead>Email</TableHead></TableRow></TableHeader>
                <TableBody>
                  {closers.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-slate-500 py-6">No closers.</TableCell></TableRow>}
                  {closers.map((c) => {
                    const ag = agencies.find((a) => a.id === c.agency_id);
                    return <TableRow key={c.id}><TableCell>{c.name}</TableCell><TableCell className="text-xs">{ag?.name || "—"}</TableCell><TableCell className="text-xs font-mono">{c.email || "—"}</TableCell></TableRow>;
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="reconciliations">
            <div className="flex justify-end mb-3"><Button size="sm" onClick={() => setOpenRec(true)} data-testid="new-reconciliation-button"><Upload className="w-4 h-4 mr-1.5" /> Upload agency report</Button></div>
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Agency</TableHead><TableHead>Period</TableHead><TableHead>File</TableHead><TableHead>Uploaded</TableHead><TableHead className="text-right">Lines</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {reconciliations.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">No reconciliations yet.</TableCell></TableRow>}
                  {reconciliations.map((r) => {
                    const ag = agencies.find((a) => a.id === r.agency_id);
                    return (
                      <TableRow key={r.id} className="cursor-pointer" onClick={() => setOpenRecDetail(r.id)}>
                        <TableCell>{ag?.name || "—"}</TableCell>
                        <TableCell className="text-xs font-mono">{r.period}</TableCell>
                        <TableCell className="text-xs font-mono">{r.filename}</TableCell>
                        <TableCell className="text-xs font-mono">{fmtDateTime(r.created_at)}</TableCell>
                        <TableCell className="text-right font-mono">{r.line_count}</TableCell>
                        <TableCell className="text-xs">{r.status}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </PageBody>

      <NewAgencyDialog open={openAg} onOpenChange={setOpenAg} onCreated={() => { setOpenAg(false); load(); }} />
      <NewCloserDialog open={openCl} onOpenChange={setOpenCl} agencies={agencies} onCreated={() => { setOpenCl(false); load(); }} />
      <UploadReconDialog open={openRec} onOpenChange={setOpenRec} agencies={agencies} onDone={() => { setOpenRec(false); load(); }} />
      <ReconDetailDialog id={openRecDetail} onOpenChange={(o) => !o && setOpenRecDetail(null)} onDone={() => { setOpenRecDetail(null); load(); }} />
    </>
  );
}

function NewAgencyDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ code: "", name: "", cohort: "elitecloser_13", rate_pct: 13 });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/agencies", { ...form, rate_pct: parseFloat(form.rate_pct) });
      toast.success("Agency created");
      setForm({ code: "", name: "", cohort: "elitecloser_13", rate_pct: 13 });
      onCreated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New agency</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase">Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="agency-code" /></div>
          <div><Label className="text-xs uppercase">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agency-name" /></div>
          <div><Label className="text-xs uppercase">Cohort</Label>
            <Select value={form.cohort} onValueChange={(v) => setForm({ ...form, cohort: v, rate_pct: v === "legacy_10" ? 10 : v === "elitecloser_13" ? 13 : form.rate_pct })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="legacy_10">Legacy 10%</SelectItem>
                <SelectItem value="elitecloser_13">EliteCloser 13%</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase">Rate %</Label><Input type="number" step="0.1" value={form.rate_pct} onChange={(e) => setForm({ ...form, rate_pct: e.target.value })} className="font-mono" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="agency-submit">{saving ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewCloserDialog({ open, onOpenChange, agencies, onCreated }) {
  const [form, setForm] = useState({ agency_id: "", name: "", email: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.agency_id || !form.name) { toast.error("Agency and name required"); return; }
    setSaving(true);
    try { await api.post("/agencies/closers", form); toast.success("Closer added"); onCreated(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New closer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase">Agency</Label>
            <Select value={form.agency_id} onValueChange={(v) => setForm({ ...form, agency_id: v })}>
              <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>{agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="closer-name" /></div>
          <div><Label className="text-xs uppercase">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="closer-submit">{saving ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadReconDialog({ open, onOpenChange, agencies, onDone }) {
  const [agencyId, setAgencyId] = useState("");
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!agencyId || !period || !file) { toast.error("Fill all fields"); return; }
    setSaving(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/agencies/reconciliations?agency_id=${agencyId}&period=${period}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Reconciliation uploaded");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Upload agency report</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label className="text-xs uppercase">Agency</Label>
            <Select value={agencyId} onValueChange={setAgencyId}>
              <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>{agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase">Period (YYYY-MM)</Label><Input value={period} onChange={(e) => setPeriod(e.target.value)} className="font-mono" /></div>
          <p className="text-xs text-slate-500">CSV columns: <code className="font-mono">sale_number, closer, commission, currency</code></p>
          <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="recon-file" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="recon-submit">{saving ? "Uploading…" : "Upload"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReconDetailDialog({ id, onOpenChange, onDone }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    if (!id) return;
    (async () => { const { data } = await api.get(`/agencies/reconciliations/${id}`); setDoc(data); })();
  }, [id]);
  if (!id) return null;
  const markReconciled = async () => {
    try { await api.post(`/agencies/reconciliations/${id}/mark-reconciled`); toast.success("Marked reconciled"); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  return (
    <Dialog open={!!id} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Reconciliation {doc?.period}</DialogTitle></DialogHeader>
        {doc && (
          <div className="text-sm space-y-3">
            <div className="text-xs text-slate-500">{doc.filename} · {doc.status}</div>
            <div className="border border-slate-200 rounded overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Sale</TableHead><TableHead>Closer</TableHead><TableHead className="text-right">Reported</TableHead><TableHead className="text-right">EliteOS</TableHead><TableHead className="text-right">Variance</TableHead></TableRow></TableHeader>
                <TableBody>
                  {doc.lines.map((l, i) => (
                    <TableRow key={`${l.sale_ref || "line"}-${i}`}>
                      <TableCell className="font-mono text-xs">{l.sale_ref}</TableCell>
                      <TableCell className="text-xs">{l.closer || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{fmtMoney(l.reported_amount_minor, l.reported_currency)}</TableCell>
                      <TableCell className="text-right font-mono">{fmtMoney(l.internal_amount_minor, l.reported_currency)}</TableCell>
                      <TableCell className={"text-right font-mono " + (l.variance_minor === 0 ? "text-emerald-700" : "text-rose-700")}>
                        {fmtMoney(l.variance_minor, l.reported_currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
              {doc.status !== "reconciled" && (
                <Button onClick={markReconciled} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mark-reconciled">Mark reconciled</Button>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
